<template lang='pug'>
  .long-tail-keywords-show
    section.page-header
      h1.page-title(v-t='{ path: "longTailKeywords.header", args: { keyword } }')
      h5.h5.page-subtitle(v-t='"longTailKeywords.subHeader"')
      p.related-keywords-container(v-if='relatedKeywordsCount > 0')
        span(v-t='{ path: "longTailKeywords.relatedKeyword", args: { domain } }')
        router-link.related-keyword(
          v-for='(relatedKeyword, index) in relatedKeywords'
          :key='relatedKeyword.id'
          :to='relatedKeyword.id'
          :target='isMobile ? null : "_blank"'
          :rel='isMobile ? null : "noopener"'
        )
          span(v-text="relatedKeyword.attributes.keyword")
          span(v-if='(index + 1) !== relatedKeywordsCount') &comma;&nbsp;

    Loader(v-if='loading')
    template(v-else)
      ProductGridEmpty(v-if='productsCount === 0')
      section.horizontal-product-grid(v-else)
        HorizontalProductCard(
          v-for='product in products'
          :key='product.id'
          :product='product'
          :variant='featuredVariantForLTKWProduct(product)'
          :review='featuredReviewForLTKWProduct(product)'
        )
</template>

<i18n>
{
  "lng_en": {
    "longTailKeywords": {
      "header": "Buy %{keyword} At Sephora!",
      "subHeader": "Discover the best in makeup, skincare, and haircare.",
      "seo": {
        "title": "Buy %{keyword}",
        "description": "Buy %{keyword} at Sephora now! Discover the best in makeup, skincare and haircare."
      },
      "relatedKeyword": "Related on %{domain}: "
    }
  },
  "lng_id": {
    "longTailKeywords": {
      "header": "Beli %{keyword} Di Sephora!",
      "subHeader": "Temukan yang terbaik dari makeup, perawatan kulit, dan perawatan rambut.",
      "seo": {
        "title": "Beli %{keyword}",
        "description": "Beli %{keyword} di Sephora sekarang! Temukan yang terbaik di makeup, perawatan kulit, dan rambut."
      },
      "relatedKeyword": "Terkait dengan %{domain}: "
    }
  },
  "lng_th": {
    "longTailKeywords": {
      "header": "ซื้อ %{keyword} ที่เซโฟรา!",
      "subHeader": "พบกับเครื่องสำอาง, สกินแคร์ และผลิตภัณฑ์ดูแลเส้นผมที่ดีที่สุด",
      "seo": {
        "title": "ซื้อ %{keyword}",
        "description": "ซื้อ %{keyword} ที่ Sephora ตอนนี้เลย! พบกับเครื่องสำอาง, สกินแคร์ และแฮร์แคร์ที่ดีที่สุด"
      },
      "relatedKeyword": "เกี่ยวข้องตาม %{domain}: "
    }
  },
  "lng_zh": {
    "longTailKeywords": {
      "header": "於Sephora選購%{keyword}！",
      "subHeader": "探索最佳化妝品、護膚品及美髮產品",
      "seo": {
        "title": "選購%{keyword}",
        "description": "即到Sephora選購%{keyword}！探索最新化妝、護膚及美髮產品。"
      },
      "relatedKeyword": "%{domain}相關結果: "
    }
  },
  "lng_ko": {
    "longTailKeywords": {
      "header": "세포라에서 %{keyword}를 구매하세요!",
      "subHeader": "메이크업, 스킨케어, 헤어케어 각 카테고리 베스트 제품들을 만나보세요!",
      "seo": {
        "title": "세포라에서 %{keyword}를",
        "description": "세포라에서 %{keyword}를 구매하세요! 다양한 카테고리 베스트 제품을 만나보세요."
      },
      "relatedKeyword": "%{domain} 관련: "
    }
  }
}
</i18n>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import capitalize from 'lodash/capitalize'
import lowerCase from 'lodash/lowerCase'
import startCase from 'lodash/startCase'
import kebabCase from 'lodash/kebabCase'
import HorizontalProductCard from '@/components/products/horizontal-product-card.vue'
import Loader from '@/components/shared/loader.vue'
import ProductGridEmpty from '@/components/products/product-grid-empty.vue'
import { notifyBugsnag } from '@/modules/services/bugsnag'
import { fullUrl } from '@/modules/services/utilities'
import { dynamicLoadStore } from '@/mixins/store-util'
import longTailKeyword from '@/store/modules/long-tail-keyword'

export default {
  components: {
    Loader,
    HorizontalProductCard,
    ProductGridEmpty
  },

  mixins: [
    dynamicLoadStore({ longTailKeyword })
  ],

  data () {
    return {
      loading: false
    }
  },

  computed: {
    ...mapState({
      longTailKeyword: state => state.longTailKeyword.longTailKeyword,
      relatedKeywords: state => state.longTailKeyword.relatedKeywords,
      products: state => state.longTailKeyword.products
    }),

    ...mapGetters([
      'featuredVariantForLTKWProduct',
      'featuredReviewForLTKWProduct'
    ]),

    domain () {
      return capitalize(this.$t('domain', I18n.siteCountryLs).replace('www.', ''))
    },

    keyword () {
      return startCase(this.$route.params.longTailKeyword)
    },

    keywordSlug () {
      return kebabCase(this.$route.params.longTailKeyword)
    },

    productsCount () {
      return this.products.length
    },

    isMobile: () => vueSupport.isMobile,

    relatedKeywordsCount () {
      return this.relatedKeywords.length
    }
  },

  watch: {
    '$route' (to, from) {
      if (this.isMobile) {
        this.fetchLongTailKeywordProducts()
      }
    }
  },

  created () {
    if (!this.longTailKeyword || this.longTailKeyword.id !== this.keywordSlug) {
      this.fetchLongTailKeywordProducts()
    }
  },

  mounted () {
    this.updateSEO()
  },

  methods: {
    ...mapMutations([
      'setNotification'
    ]),

    async fetchLongTailKeywordProducts () {
      try {
        this.loading = true
        await this.$store.dispatch('fetchLongTailKeyword', this.keywordSlug)
        const categoryId = this.longTailKeyword.relationships.category.data && this.longTailKeyword.relationships.category.data.id
        await this.$store.dispatch('fetchLongTailKeywordProducts', {
          keyword: lowerCase(this.keyword),
          categoryId: categoryId
        })
      } catch (error) {
        this.setNotification({ msg: this.$t('notifications.error'), type: 'error' })
        notifyBugsnag(error)
      } finally {
        this.loading = false
      }
    },

    updateSEO () {
      const keyword = this.keyword

      this.$store.commit('setSeoMetaTitle', this.$t('longTailKeywords.seo.title', { keyword }))
      this.$store.commit('setSeoMetaDescription', this.$t('longTailKeywords.seo.description', { keyword }))
      this.$store.commit('setSeoOgUrl', fullUrl(this.$route.path))
      this.$store.commit('setSeoUpdate', true)
    }
  }
}
</script>

<style lang='scss' scoped>
  .long-tail-keywords-show {
    .page-header {
      margin-bottom: 10px;

      .page-title {
        margin-top: 5px;
        margin-bottom: 5px;
      }

      .page-subtitle {
        line-height: 20px;
      }

      .related-keywords-container {
        line-height: 25px;
      }

      .related-keyword span:first-child {
        text-decoration: underline;
      }
    }

    .horizontal-product-grid {
      margin-top: 10px;
    }
  }
</style>
